/*
 * globals.css — the single stylesheet imported from main.tsx.
 *
 * Import order matters:
 *   1. Tailwind v4 (must come first to register layers)
 *   2. HeroUI styles (consume Tailwind layers)
 *   3. The three theme files (override HeroUI's :root tokens per data-theme)
 *
 * Active theme is selected by `<html data-theme="clean|green|zixiaolabsvi">`
 * with an optional `class="dark"` modifier; index.html restores both before
 * paint to avoid FOUC.
 */

@import "tailwindcss";
@import "@heroui/styles";

@import "./theme-clean.css";
@import "./theme-green.css";
@import "./theme-zixiaolabsvi.css";

/* ---------------------------------------------------------------------------
 * Source declarations — without these, nasti's Tailwind plugin only scans
 * CSS files (its `oxide.Scanner` is fed `compiler.sources`, which defaults
 * to nothing). We point it at every JSX/TS source under `src/` so the
 * utility classes used in component code actually get generated.
 * Paths are relative to THIS file (`src/styles/globals.css`).
 * --------------------------------------------------------------------------- */
@source "../**/*.{ts,tsx,jsx,js}";
@source "../../index.html";

/* ---------------------------------------------------------------------------
 * Tailwind v4 bridge — map existing theme CSS variables to utility classes.
 * `inline` keeps the var() reference live so theme switching is reactive.
 * We only ALIAS variables already defined in theme-*.css; no new colors are
 * introduced here.
 * --------------------------------------------------------------------------- */
@theme inline {
  /* Surfaces & background */
  --color-bg:                 var(--background);
  --color-surface:            var(--surface);
  --color-surface-2:          var(--surface-secondary);
  --color-surface-3:          var(--surface-tertiary);
  --color-overlay:            var(--overlay);

  /* Borders & dividers */
  --color-border:             var(--border);
  --color-separator:          var(--separator);

  /* Text */
  --color-fg:                 var(--foreground);
  --color-muted:              var(--muted);

  /* Brand & semantic */
  --color-accent:             var(--accent);
  --color-accent-fg:          var(--accent-foreground);
  --color-danger:             var(--danger);
  --color-danger-fg:          var(--danger-foreground);
  --color-success:            var(--success);
  --color-success-fg:         var(--success-foreground);
  --color-warning:            var(--warning);
  --color-warning-fg:         var(--warning-foreground);
  --color-focus:              var(--focus);

  /* Fields / inputs */
  --color-field:              var(--field-background);
  --color-field-fg:           var(--field-foreground);
  --color-field-placeholder:  var(--field-placeholder);

  /* Radii — alias only, theme files own the actual values */
  --radius-token:             var(--radius);
  --radius-field:             var(--field-radius);

  /* Font */
  --font-sans:                var(--font-sans);
  --font-mono:                "JetBrains Mono", "SF Mono", ui-monospace,
                              Menlo, Consolas, monospace;
}

html,
body,
#root {
  height: 100%;
}

body {
  margin: 0;
  background: var(--background);
  color: var(--foreground);
  font-family: var(--font-sans, system-ui, sans-serif);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

/* Small typography defaults for raw markdown rendering. */
.wuling-prose h1 { font-size: 1.75rem; font-weight: 700; margin: 1rem 0 0.5rem; }
.wuling-prose h2 { font-size: 1.5rem;  font-weight: 700; margin: 1rem 0 0.5rem; }
.wuling-prose h3 { font-size: 1.25rem; font-weight: 600; margin: 0.75rem 0 0.5rem; }
.wuling-prose h4 { font-size: 1.125rem; font-weight: 600; margin: 0.75rem 0 0.5rem; }
.wuling-prose h5 { font-size: 1rem; font-weight: 600; margin: 0.5rem 0 0.25rem; }
.wuling-prose h6 { font-size: 0.875rem; font-weight: 600; margin: 0.5rem 0 0.25rem; color: var(--muted); }
.wuling-prose p  { margin: 0.5rem 0; line-height: 1.65; }
.wuling-prose code {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  background: var(--surface-secondary);
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
}
.wuling-prose pre {
  background: var(--surface-secondary);
  padding: 0.75rem;
  border-radius: 0.5rem;
  overflow-x: auto;
}
.wuling-prose pre code { background: transparent; padding: 0; }
.wuling-prose a { color: var(--accent); text-decoration: underline; }
.wuling-prose ul, .wuling-prose ol { padding-left: 1.5rem; margin: 0.5rem 0; }
.wuling-prose blockquote {
  border-left: 3px solid var(--border);
  padding-left: 0.75rem;
  color: var(--muted);
  margin: 0.5rem 0;
}
.wuling-prose table { border-collapse: collapse; }
.wuling-prose table td, .wuling-prose table th {
  border: 1px solid var(--border);
  padding: 0.4rem 0.6rem;
}
.wuling-prose table th { font-weight: 600; background: var(--surface-secondary); }
.wuling-prose hr { border: none; border-top: 1px solid var(--border); margin: 1.5rem 0; }
.wuling-prose img { max-width: 100%; height: auto; border-radius: 0.375rem; }

/* Screen-reader-only utility (help centre search label). */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
